import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, List, ListItemButton, ListItemText,
  Stack, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment } from '@mui/material';
import { quoteBlocksAPI } from '../../services/api';
import { renderRichHtml } from '../../utils/sanitizeHtml';

/**
 * Auswahl aus der Bausteinbibliothek. Mehrfachauswahl, damit mehrere Bausteine
 * in einem Rutsch (und einem Request) im Angebot landen.
 */
function BlockLibraryDialog({ open, onClose, onInsert }) {
  const [blocks, setBlocks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cats] = await Promise.all([
        quoteBlocksAPI.getAll({}),
        quoteBlocksAPI.getCategories(),
      ]);
      setBlocks(list.data || []);
      setCategories(cats.data || []);
    } catch (err) {
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      load();
      setSelected([]);
      setTerm('');
      setCategory(null);
      setPreview(null);
    }
  }, [open, load]);

  // Clientseitig filtern: die Bibliothek ist klein, das spart einen Request
  // pro Tastendruck und fühlt sich unmittelbar an.
  const needle = term.trim().toLowerCase();
  const visible = blocks.filter((b) => {
    if (category && b.category !== category) return false;
    if (!needle) return true;
    return `${b.title} ${b.category || ''}`.toLowerCase().includes(needle);
  });

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bausteine aus der Bibliothek</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', gap: 2, minHeight: 420 }}>
        <Box sx={{ width: '55%' }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Suchen …"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              size="small"
              label="Alle"
              color={category === null ? 'primary' : 'default'}
              onClick={() => setCategory(null)}
            />
            {categories.map((c) => (
              <Chip
                key={c}
                size="small"
                label={c}
                color={category === c ? 'primary' : 'default'}
                onClick={() => setCategory(c)}
              />
            ))}
          </Stack>
          <Divider sx={{ my: 1 }} />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : visible.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              Noch keine Bausteine. Über das Menü eines Bausteins lässt sich einer
              als Vorlage speichern.
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
              {visible.map((b) => (
                <ListItemButton
                  key={b.id}
                  selected={preview?.id === b.id}
                  onClick={() => setPreview(b)}
                >
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={selected.includes(b.id)}
                    onClick={(e) => { e.stopPropagation(); toggle(b.id); }}
                  />
                  <ListItemText
                    primary={b.title}
                    secondary={[b.category, b.default_days ? `${b.default_days} PT` : null]
                      .filter(Boolean).join(' · ') || null}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ width: '45%', overflow: 'auto' }}>
          {preview ? (
            <>
              <Typography variant="subtitle2" gutterBottom>{preview.title}</Typography>
              <Box
                sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                dangerouslySetInnerHTML={{ __html: renderRichHtml(preview.body_html) }}
              />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Einen Baustein anklicken, um ihn hier zu lesen.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant="contained"
          disabled={selected.length === 0}
          onClick={() => onInsert(selected)}
        >
          {selected.length === 1 ? '1 Baustein einfügen' : `${selected.length} Bausteine einfügen`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BlockLibraryDialog;
